import {
  addDisplayName,
  addEffect,
  addProps,
  addRef,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import Hls from 'hls.js'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { typedAs } from 'shared/src/utils/typedAs'
import { FeedVideoContent } from 'shared/src/api/types'

import { addFeedContext } from '../context'
import playIcon from 'assets/play.svg'
import { ImageButton } from '../../ImageButton'
import { PLAY_ICON_Z_INDEX, VIDEO_PLAYER_CONTAINER_Z_INDEX } from 'utils/zIndex'

const useStyles = createUseStyles({
  container: {
    position: 'relative',
  },
  videoContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    width: '100%',
    height: '100%',
    zIndex: VIDEO_PLAYER_CONTAINER_Z_INDEX,
  },
  video: {
    position: 'absolute',
    left: 0,
    right: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  playButtonContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: PLAY_ICON_Z_INDEX,
  },
})

interface Props {
  video: FeedVideoContent
  isActive: boolean
  className?: string
}

export const Video: FC<Props> = flowMax(
  addDisplayName('Video'),
  addProps(({ video: { value } }) => ({
    videoUrl: `https://stream.mux.com/${value}.m3u8`,
    thumbnailUrl: `https://image.mux.com/${value}/thumbnail.jpg`,
  })),
  addRef('ref', typedAs<HTMLVideoElement | null>(null)),
  addState('isVideoElementPlaying', 'setVideoElementPlaying', false),
  addEffect(
    ({ videoUrl, ref, setVideoElementPlaying }) =>
      () => {
        let hls: Hls | null = null

        if (!ref.current) {
          return
        }

        const video = ref.current

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = videoUrl
        } else if (Hls.isSupported()) {
          hls = new Hls()
          hls.loadSource(videoUrl)
          hls.attachMedia(video)
        } else {
          console.error("This is a legacy browser that doesn't support MSE")
        }

        video.onplaying = () => {
          // setVideoElementPlaying(true)
        }

        video.onpause = () => {
          setVideoElementPlaying(false)
        }

        return () => {
          hls?.destroy()
          video.onplaying = null
        }
      },
    ['videoUrl', 'ref', 'setVideoElementPlaying']
  ),
  addFeedContext,
  addEffect(
    ({ isActive, isPlaying, ref }) =>
      () => {
        const videoElement = ref.current

        if (!videoElement) {
          return
        }

        if (isActive && isPlaying) {
          videoElement.play().catch(() => {
            // Async "play()" might be interrupted by calling "pause()" and error,
            // so ignore errors
          })
        } else {
          videoElement.pause()
        }
      },
    ['isActive', 'isPlaying', 'ref']
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    ref,
    thumbnailUrl,
    isMuted,
    isPlaying,
    togglePlaying,
    classes,
    className,
  }) => (
    <div className={cx(classes.container, className)}>
      <img src={thumbnailUrl} alt="" className={classes.thumbnail} />
      <div className={classes.videoContainer} onClick={togglePlaying} />
      <video
        playsInline
        loop
        muted={isMuted}
        poster={thumbnailUrl}
        ref={ref}
        onClick={togglePlaying}
        className={classes.video}
      />
      {!isPlaying && (
        <div className={classes.playButtonContainer}>
          <ImageButton
            onClick={togglePlaying}
            image={playIcon}
            width={96}
            height={96}
          />
        </div>
      )}
    </div>
  )
)
